import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Briefcase, Users, DollarSign, Scale,
  BarChart3, Activity, RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const STATUS_COLORS = {
  OPEN: '#3b82f6', IN_PROGRESS: '#f59e0b', CLOSED: '#10b981', RESOLVED: '#06b6d4',
  PREVENTIVE: '#ef4444', SENTENCED: '#8b5cf6', RELEASED: '#10b981', TRANSFERRED: '#f59e0b',
  PENDING: '#f59e0b', COMPLETED: '#10b981', FAILED: '#ef4444'
};

const KpiCard = ({ title, value, icon: Icon, color, change, prefix = '', suffix = '' }) => (
  <Card className="relative overflow-hidden" data-testid={`kpi-${title.replace(/\s/g, '-').toLowerCase()}`}>
    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${color}`} />
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold mt-1 tracking-tight">{prefix}{typeof value === 'number' ? value.toLocaleString('fr-FR') : value}{suffix}</p>
        </div>
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${color} text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {change >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
          {Math.abs(change)}%
          <span className="text-muted-foreground ml-1">vs période préc.</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background/95 backdrop-blur-sm border rounded-xl p-3 shadow-xl">
      <p className="font-semibold text-sm mb-1.5">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-semibold">{typeof entry.value === 'number' ? entry.value.toLocaleString('fr-FR') : entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsDashboardPage = () => {
  const { language } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('12');

  useEffect(() => { loadData(); }, [months]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/dashboard/analytics/trends`, {
        headers: getAuth(), params: { months: parseInt(months) }
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Chargement des analyses...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const { trends, kpis, cases_by_status, prisoners_by_status, top_jurisdictions, payment_methods } = data;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Tableau de Bord Analytique
            </h1>
            <p className="text-muted-foreground mt-1">
              Vue d'ensemble des tendances et performances
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger className="w-[160px]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 derniers mois</SelectItem>
                <SelectItem value="6">6 derniers mois</SelectItem>
                <SelectItem value="12">12 derniers mois</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard title="Total Dossiers" value={kpis.total_cases} icon={Briefcase} color="from-blue-500 to-blue-600" change={kpis.resolution_rate > 50 ? 12 : -5} />
          <KpiCard title="Détenus Actifs" value={kpis.total_prisoners} icon={Users} color="from-amber-500 to-orange-500" />
          <KpiCard title="Taux Résolution" value={kpis.resolution_rate} icon={Scale} color="from-emerald-500 to-teal-500" suffix="%" />
          <KpiCard title="Revenus" value={kpis.total_revenue} icon={DollarSign} color="from-violet-500 to-purple-600" prefix="" suffix=" USD" />
        </div>

        {/* Main Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trends Line Chart - takes 2 columns */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  Tendances Mensuelles
                </CardTitle>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 rounded" /> Dossiers</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 rounded" /> Détentions</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 rounded" /> Paiements</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trends} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradDossiers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradDetentions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradPaiements" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="dossiers" name="Dossiers" stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradDossiers)" dot={{ r: 3, fill: '#3b82f6' }} />
                  <Area type="monotone" dataKey="detentions" name="Détentions" stroke="#f59e0b" strokeWidth={2.5} fill="url(#gradDetentions)" dot={{ r: 3, fill: '#f59e0b' }} />
                  <Area type="monotone" dataKey="paiements" name="Paiements" stroke="#10b981" strokeWidth={2.5} fill="url(#gradPaiements)" dot={{ r: 3, fill: '#10b981' }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Cases by Status - Pie Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Dossiers par Statut</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={cases_by_status.filter(s => s.value > 0)}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {cases_by_status.filter(s => s.value > 0).map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(val) => <span className="text-xs">{val}</span>}
                    iconType="circle"
                    iconSize={8}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Trends */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                Revenus Mensuels (USD)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trends} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenus" name="Revenus" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {trends.map((entry, i) => (
                      <Cell key={i} fill={entry.revenus > 0 ? '#10b981' : '#e5e7eb'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Plaintes & Libérations */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                Plaintes & Libérations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trends} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="plaintes" name="Plaintes" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3, fill: '#ef4444' }} />
                  <Line type="monotone" dataKey="liberations" name="Libérations" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3, fill: '#8b5cf6' }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Third Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Jurisdictions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Top Juridictions</CardTitle>
            </CardHeader>
            <CardContent>
              {top_jurisdictions?.length > 0 ? (
                <div className="space-y-3">
                  {top_jurisdictions.slice(0, 8).map((j, i) => {
                    const max = top_jurisdictions[0]?.total || 1;
                    const pct = Math.round((j.total / max) * 100);
                    return (
                      <div key={i} className="space-y-1" data-testid={`jurisdiction-${i}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate max-w-[200px] font-medium">{j.name || 'Non spécifié'}</span>
                          <span className="font-mono text-xs text-muted-foreground">{j.total} dossiers</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Prisoners by Status + Payment Methods */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Détenus par Statut</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={prisoners_by_status.filter(s => s.value > 0)} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} stroke="var(--muted-foreground)" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Détenus" radius={[0, 6, 6, 0]} maxBarSize={24}>
                      {prisoners_by_status.filter(s => s.value > 0).map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Méthodes de Paiement</CardTitle>
              </CardHeader>
              <CardContent>
                {payment_methods?.length > 0 ? (
                  <div className="space-y-2">
                    {payment_methods.map((pm, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 border border-border/30">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-sm font-medium capitalize">{pm.name || 'Carte'}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{pm.count} tx</p>
                          <p className="text-xs text-muted-foreground">{pm.total?.toLocaleString('fr-FR')} USD</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune transaction</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboardPage;
